import type { GettingStartedStepKey } from '@/lib/services/onboarding/types';

/**
 * The six canonical Getting Started steps, in display order.
 *
 * **One copy of the backend's `GETTING_STARTED_STEPS`**
 * (`backend/src/onboarding/getting-started-steps.ts`), which the packages cannot
 * share by import. `__tests__/getting-started-registry.test.ts` reconciles
 * this list against the live API response in both directions, so a seventh step
 * added on either side fails a test rather than shipping half-wired.
 */
export const CANONICAL_STEPS = [
  'setup-firm',
  'navigate',
  'create-matter',
  'intake-form',
  'ai-scribe',
  'notify-institutions'
] as const satisfies readonly GettingStartedStepKey[];

export const TOTAL_STEPS = CANONICAL_STEPS.length;

export interface GettingStartedStepDescriptor {
  id: GettingStartedStepKey;
  /** The long form, used by the hub. */
  title: string;
  /** The compact label for the icon rail's tooltip. */
  shortTitle: string;
  description: string;
}

/**
 * Every step's copy, including `setup-firm`.
 *
 * **`setup-firm` is in this list rather than behind its own label function**, so
 * the hub and the rail map over one uniform array of six. It used to be
 * special-cased in both, which is how the hub grew a bespoke row with an
 * "In progress" badge that no longer has anything to read: the badge came from
 * the browser-local "you clicked this once" array, and completion is now what
 * the user actually did (VALE-1830).
 *
 * `setup-firm`'s copy says "See how your letters and emails look" rather than
 * anything about uploading: Vale configures a firm's letterhead before anyone
 * signs in and Settings owns changes to it, so the step reviews what goes out
 * under the reader's name (VALE-1633).
 */
export const GETTING_STARTED_STEPS: readonly GettingStartedStepDescriptor[] = [
  {
    id: 'setup-firm',
    title: 'See how your letters and emails look',
    shortTitle: 'Letters and emails',
    description: 'Check your details, signature and letterhead'
  },
  {
    id: 'navigate',
    title: 'Navigate Vale with our guided tour',
    shortTitle: 'Navigate Vale',
    description: 'A walkthrough of the navigation, key sections, and layout'
  },
  {
    id: 'create-matter',
    title: 'Create a matter',
    shortTitle: 'Create a matter',
    description: 'Create your first probate matter and learn the basics'
  },
  {
    id: 'intake-form',
    title: 'Send a client intake form',
    shortTitle: 'Send intake form',
    description:
      'Send an intake form to the client and import the returned data'
  },
  {
    id: 'ai-scribe',
    title: 'Try AI Scribe',
    shortTitle: 'Try AI Scribe',
    description:
      'Record or upload a client meeting and import AI-transcribed data'
  },
  {
    id: 'notify-institutions',
    title: 'Notify institutions',
    shortTitle: 'Notify institutions',
    description:
      'Send notifications to relevant institutions (banks, super funds, etc.)'
  }
] as const;
